import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, Lock, Database, Mail, CreditCard, UserX, Globe } from 'lucide-react';
import { cn } from '../lib/utils';

const LAST_UPDATED = 'December 2025';

const sections = [
  {
    icon: Shield,
    title: '1. Introduction',
    body: (
      <>
        <p>
          Welcome to Monk Mode ("we", "us", "our"). We are committed to protecting your privacy
          and handling your personal data transparently. This Privacy Policy explains what
          information we collect, how we use it, and the choices you have.
        </p>
        <p className="mt-3">
          By creating an account or using Monk Mode, you agree to the practices described below.
        </p>
      </>
    ),
  },
  {
    icon: Database,
    title: '2. Information We Collect',
    body: (
      <>
        <p className="font-medium text-base mb-2">Information you provide</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Account details: email address, password (hashed), display name.</li>
          <li>Goals and progress data you create within the app.</li>
          <li>Payment details processed by Stripe (we never store card numbers).</li>
          <li>Messages you send to support.</li>
        </ul>
        <p className="font-medium text-base mt-4 mb-2">Information collected automatically</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Log data such as IP address, browser type, device, and timestamps.</li>
          <li>Usage analytics (pages viewed, actions taken) to improve the product.</li>
          <li>Cookies / local storage to keep you signed in and remember preferences.</li>
        </ul>
      </>
    ),
  },
  {
    icon: Lock,
    title: '3. How We Use Your Information',
    body: (
      <ul className="list-disc pl-5 space-y-1">
        <li>Provide, operate, and maintain the Monk Mode service.</li>
        <li>Authenticate you and secure your account.</li>
        <li>Process subscription payments and issue refunds where eligible.</li>
        <li>Send transactional emails (receipts, reminders, account notices).</li>
        <li>Analyze usage to improve features and fix issues.</li>
        <li>Enforce our Terms of Service and prevent abuse.</li>
      </ul>
    ),
  },
  {
    icon: Globe,
    title: '4. Third-Party Services',
    body: (
      <>
        <p>We rely on trusted providers to deliver parts of the service:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>
            <span className="font-medium">Stripe</span> — payment processing and subscription
            management. See{' '}
            <a href="https://stripe.com/privacy" target="_blank" rel="noreferrer" className="underline">
              Stripe's Privacy Policy
            </a>.
          </li>
          <li>
            <span className="font-medium">Google Sign-In</span> — optional social login. Google
            shares your name and email with us when you choose this option.
          </li>
          <li>
            <span className="font-medium">Resend</span> — transactional email delivery.
          </li>
          <li>
            <span className="font-medium">MongoDB Atlas</span> — database hosting.
          </li>
        </ul>
        <p className="mt-3">
          We do <span className="font-semibold">not</span> sell your personal information to
          advertisers or third parties.
        </p>
      </>
    ),
  },
  {
    icon: CreditCard,
    title: '5. Payments & Subscriptions',
    body: (
      <>
        <p>
          Pro subscription billing is handled by Stripe. Card details are collected and stored by
          Stripe directly and never touch our servers. We retain a subscription reference ID and
          invoice history to operate your account, process financial pledges, and manage billing.
        </p>
      </>
    ),
  },
  {
    icon: UserX,
    title: '6. Data Retention & Deletion',
    body: (
      <>
        <p>
          You can delete your account at any time from the app, which permanently removes your
          profile, goals, tracking history, and cancels any active subscription. Some records
          required by law (e.g., tax invoices) may be retained in anonymized form for the legally
          required period.
        </p>
        <p className="mt-3">
          Inactive accounts with no activity for 24 months may be purged after we attempt to
          notify you.
        </p>
      </>
    ),
  },
  {
    icon: Shield,
    title: '7. Your Rights',
    body: (
      <>
        <p>Depending on your location, you may have the right to:</p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li>Access the personal data we hold about you.</li>
          <li>Correct inaccurate data.</li>
          <li>Delete your data (via in-app account deletion or by contacting us).</li>
          <li>Export your data in a portable format.</li>
          <li>Object to or restrict certain processing activities.</li>
        </ul>
        <p className="mt-3">To exercise any of these rights, email us at the address below.</p>
      </>
    ),
  },
  {
    icon: Lock,
    title: '8. Security',
    body: (
      <p>
        Passwords are hashed with industry-standard algorithms, data is transmitted over TLS, and
        access to production systems is restricted. No online service can guarantee absolute
        security, but we work continuously to protect your data.
      </p>
    ),
  },
  {
    icon: Shield,
    title: '9. Children',
    body: (
      <p>
        Monk Mode is not intended for children under 13 (or the minimum age required by your
        jurisdiction). We do not knowingly collect data from children.
      </p>
    ),
  },
  {
    icon: Shield,
    title: '10. Changes to This Policy',
    body: (
      <p>
        We may update this policy from time to time. Material changes will be announced in-app or
        via email. Continued use of Monk Mode after a change means you accept the revised policy.
      </p>
    ),
  },
  {
    icon: Mail,
    title: '11. Contact',
    body: (
      <p>
        Questions or requests about your privacy? Email us at{' '}
        <a href="mailto:privacy@monkmode.app" className="underline">
          privacy@monkmode.app
        </a>
        .
      </p>
    ),
  },
];

export default function Privacy() {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  return (
    <div
      className={cn(
        'min-h-screen',
        isDark
          ? 'bg-[#050505] text-white'
          : 'bg-[#FAF9F5] text-[#1A1A1A]'
      )}
      data-testid="privacy-page"
    >
      <div className="max-w-3xl mx-auto px-6 py-10 sm:py-16">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className={cn('mb-6 -ml-3', isDark ? 'text-zinc-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')}
          data-testid="privacy-back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>

        <div className="mb-10">
          <div
            className={cn(
              'inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium mb-4 border',
              isDark
                ? 'border-white/10 bg-white/5 text-zinc-300'
                : 'border-violet-100 bg-white text-slate-600'
            )}
          >
            <Shield className="w-3.5 h-3.5" />
            Last updated: {LAST_UPDATED}
          </div>
          <h1
            className={cn(
              'text-4xl sm:text-5xl font-semibold tracking-tight',
              isDark ? 'text-white' : 'text-slate-900'
            )}
          >
            Privacy Policy
          </h1>
          <p className={cn('mt-3 text-base', isDark ? 'text-zinc-400' : 'text-slate-500')}>
            How Monk Mode collects, uses, and protects your information.
          </p>
        </div>

        <Card
          className={cn(
            'border',
            isDark ? 'bg-white/5 border-white/10' : 'bg-white border-violet-100 shadow-sm'
          )}
        >
          <CardContent className="p-6 sm:p-10 space-y-10">
            {sections.map((s, i) => {
              const Icon = s.icon;
              return (
                <section key={i} data-testid={`privacy-section-${i}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className={cn(
                        'w-8 h-8 rounded-lg flex items-center justify-center',
                        isDark ? 'bg-amber-500/10 text-amber-300' : 'bg-amber-100 text-amber-700'
                      )}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <h2
                      className={cn(
                        'text-lg font-semibold',
                        isDark ? 'text-white' : 'text-slate-900'
                      )}
                    >
                      {s.title}
                    </h2>
                  </div>
                  <div
                    className={cn(
                      'text-sm leading-relaxed',
                      isDark ? 'text-zinc-300' : 'text-slate-600'
                    )}
                  >
                    {s.body}
                  </div>
                </section>
              );
            })}
          </CardContent>
        </Card>

        <p
          className={cn(
            'text-center text-xs mt-8',
            isDark ? 'text-zinc-500' : 'text-slate-400'
          )}
        >
          © {new Date().getFullYear()} Monk Mode. All rights reserved.
        </p>
      </div>
    </div>
  );
}
